import { AvatarBlock, Link } from '@ksyos/design-system';
import { useSelector } from 'react-redux';
import type { IPatient } from '@common/interfaces/patient';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { getPatientAvatarIcon } from '@common/utils/getPatientAvatarIcon';
import { DisplayAge } from '@frontend/components/elements/DisplayAge';
import { NullableStringBirthDate } from '@frontend/components/elements/NullableStringBirthDate';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import { useUiStateContext } from '@frontend/utils/hooks/useUiState';

export interface IPatientAvatarBlockProps {
    patient: IPatient;
    size: 'large' | 'small';
}

export function PatientAvatarBlock(props: IPatientAvatarBlockProps) {
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const { uiState, dispatchUiStateAction } = useUiStateContext();

    function handleLinkClick(event: React.MouseEvent<HTMLElement, MouseEvent>) {
        event.stopPropagation();
        if (uiState.isTopBarOpen) {
            dispatchUiStateAction({ type: 'setIsTopBarOpen', isTopBarOpen: false });
        }
    }

    return (
        <AvatarBlock
            backgroundColor="#FFFFFF"
            icon={props.patient.gender ? getPatientAvatarIcon(props.patient.gender) : undefined}
            id="patient-avatar-block"
            size={props.size}
            subtitle={
                <>
                    {props.patient.gender && translateKey(`genderEnum.${props.patient.gender}`) + ' '}
                    {props.patient.birthDate && (
                        <>
                            <NullableStringBirthDate value={props.patient.birthDate} /> (
                            <DisplayAge value={props.patient.birthDate} />)
                        </>
                    )}
                </>
            }
            title={renderTitle()}
        />
    );

    function renderTitle() {
        if (isManager && !hasOneAuthorization(ManagerAuthorization.patientProcesses)) {
            return getDisplayName(props.patient, true);
        }

        return (
            <Link
                color="subtle"
                fontWeight="medium"
                onClick={handleLinkClick}
                textAlign={props.size === 'large' ? 'center' : undefined}
                to={`/patients/${props.patient.id}`}
                variant="link"
            >
                {getDisplayName(props.patient, true)}
            </Link>
        );
    }
}
